import { formatValue } from '@/charts/palette'
import { useT } from '@/i18n'
import { uiMessages } from '@/i18n/messages/ui'

interface Props {
  label: string
  value: number
  /** The limit the value is measured against. null when AU never recorded a plan. */
  limit: number | null
  unit: string
  color: string
}

/**
 * A single ratio against a limit.
 *
 * A meter, not a donut. The mockup drew these as rings, but a 2-slice pie is an
 * anti-pattern: it renders the same information as a one-hue track plus a fill, while
 * being harder to read and inviting comparison of arc lengths nobody can judge. The hero
 * number carries the value; the track carries the ratio.
 *
 * When `limit` is null the bar is omitted entirely rather than drawn empty or at 100%.
 * Half of AU's projects have no planned figure recorded, and a full bar against a missing
 * plan would read as "complete".
 */
export function Meter({ label, value, limit, unit, color }: Props) {
  const t = useT(uiMessages)
  const pct = limit && limit > 0 ? Math.min((value / limit) * 100, 100) : null
  const over = limit != null && limit > 0 && value > limit

  return (
    <div className="rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <div className="text-xs text-neutral-500">{label}</div>
      <div className="tabular mt-1 text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
        {formatValue(value, unit)}
      </div>

      {pct == null ? (
        <p className="mt-2 text-xs text-neutral-400">{t('noPlanRecorded')}</p>
      ) : (
        <>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
            <div
              className="h-full rounded-full"
              style={{ width: `${pct}%`, backgroundColor: over ? 'var(--viz-warning)' : color }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-xs text-neutral-500">
            <span className="tabular">{pct.toFixed(0)}%</span>
            <span className="tabular">{t('ofLimit', { limit: formatValue(limit!, unit) })}</span>
          </div>
        </>
      )}
    </div>
  )
}
